// Puerto de packages/hce/src/hce/paciente/LabsTab.tsx.
import { useMedplum, useSearchResources } from '@medplum/react-hooks';
import type { DiagnosticReport, Observation, ServiceRequest } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SelectorFormularios } from './SelectorFormularios';
import { TablaVacia } from './TablaVacia';

const VARIANTE_ESTADO_ORDEN: Record<string, 'pine' | 'secondary' | 'destructive'> = { active: 'pine', completed: 'secondary', revoked: 'destructive' };

/**
 * Detalle de una solicitud (010A/012A/013A/118/...) — bug real reportado en
 * vivo 2026-09-05: la tabla de Solicitudes solo mostraba código/categoría/
 * estado, sin forma de leer el contenido completo ya guardado. El texto
 * completo YA vive en `code.text` (los discretos lo arman ahí, ver
 * Formulario010A.tsx) — este diálogo solo lo expone de forma legible en vez
 * de solo la primera línea de la celda de la tabla.
 */
function DetalleSolicitudDialog({ solicitud, onOpenChange }: { readonly solicitud: ServiceRequest | null; readonly onOpenChange: (v: boolean) => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <Dialog open={solicitud !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{solicitud?.category?.[0]?.text ?? t('fichaPaciente.solicitudes')}</DialogTitle>
        </DialogHeader>
        {solicitud && (
          <div className="flex flex-col gap-2 text-sm text-hce-ink">
            <p className="whitespace-pre-wrap">{solicitud.code?.text ?? '—'}</p>
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-hce-muted">
              <dt>{t('fichaPaciente.columnas.fecha')}</dt>
              <dd>{solicitud.authoredOn ? new Date(solicitud.authoredOn).toLocaleString('es-EC') : '—'}</dd>
              {solicitud.occurrenceDateTime && (
                <>
                  <dt>{t('formularios.f010a.fechaToma')}</dt>
                  <dd>{new Date(solicitud.occurrenceDateTime).toLocaleDateString('es-EC')}</dd>
                </>
              )}
              <dt>{t('formularios.f010a.prioridad')}</dt>
              <dd className="capitalize">{solicitud.priority ?? '—'}</dd>
              <dt>{t('fichaPaciente.columnas.estado')}</dt>
              <dd>{solicitud.status ? t(`fichaPaciente.estadoSolicitud.${solicitud.status}`, solicitud.status) : '—'}</dd>
            </dl>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Detalle de un informe (010B/012B/013B/...) — mismo bug/fix que la solicitud
 * de arriba. La conclusión ya se ve en la tabla, pero los resultados
 * discretos (`DiagnosticReport.result`, un `Observation` por fila del
 * formulario, ver Formulario010B.tsx) no se mostraban en ningún lado.
 */
function DetalleInformeDialog({ informe, onOpenChange }: { readonly informe: DiagnosticReport | null; readonly onOpenChange: (v: boolean) => void }): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const [resultados, setResultados] = useState<Observation[]>([]);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    if (!informe?.result?.length) {
      setResultados([]);
      return;
    }
    let cancelado = false;
    setCargando(true);
    Promise.all(informe.result.map((r) => medplum.readReference<Observation>(r)))
      .then((r) => {
        if (!cancelado) setResultados(r);
      })
      .catch(() => {
        if (!cancelado) setResultados([]);
      })
      .finally(() => {
        if (!cancelado) setCargando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [informe, medplum]);

  return (
    <Dialog open={informe !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{informe?.code?.text ?? t('fichaPaciente.informes')}</DialogTitle>
        </DialogHeader>
        {informe && (
          <div className="flex flex-col gap-2 text-sm text-hce-ink">
            {informe.conclusion && <p className="whitespace-pre-wrap">{informe.conclusion}</p>}
            {cargando ? (
              <p className="text-sm text-muted-foreground">{t('fichaPaciente.cargando')}</p>
            ) : (
              resultados.length > 0 && (
                <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  {resultados.map((o) => (
                    <Fragment key={o.id}>
                      <dt className="text-hce-muted">{o.code?.text ?? '—'}</dt>
                      <dd>
                        {o.valueString ?? '—'}
                        {o.referenceRange?.[0]?.text ? ` (ref: ${o.referenceRange[0].text})` : ''}
                      </dd>
                    </Fragment>
                  ))}
                </dl>
              )
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function LabsTab(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams() as { id: string };
  const [solicitudes, cargandoSolicitudes] = useSearchResources('ServiceRequest', { patient: `Patient/${id}`, _sort: '-authored', _count: 100 });
  const [informes, cargandoInformes] = useSearchResources('DiagnosticReport', { patient: `Patient/${id}`, _sort: '-issued', _count: 100 });
  const [abierto, setAbierto] = useState(false);
  const [solicitudAbierta, setSolicitudAbierta] = useState<ServiceRequest | null>(null);
  const [informeAbierto, setInformeAbierto] = useState<DiagnosticReport | null>(null);

  const listaSolicitudes = (solicitudes ?? []) as ServiceRequest[];
  const listaInformes = (informes ?? []) as DiagnosticReport[];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h5 className="text-sm font-semibold">{t('fichaPaciente.tabs.labs')}</h5>
        <Button size="sm" variant={abierto ? 'secondary' : 'default'} onClick={() => setAbierto((v) => !v)}>
          {abierto ? t('fichaPaciente.notas.cerrarNuevaNota') : t('fichaPaciente.notas.nuevaSolicitud')}
        </Button>
      </div>
      <Collapsible open={abierto}>
        <CollapsibleContent>
          <SelectorFormularios subject={`Patient/${id}`} soloGrupo="diagnostico" />
        </CollapsibleContent>
      </Collapsible>

      <h5 className="mt-2 text-sm font-semibold">{t('fichaPaciente.solicitudes')}</h5>
      {cargandoSolicitudes ? (
        <p className="text-sm text-muted-foreground">{t('fichaPaciente.cargando')}</p>
      ) : listaSolicitudes.length === 0 ? (
        <TablaVacia mensaje={t('fichaPaciente.sinDatos')} />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('fichaPaciente.columnas.fecha')}</TableHead>
              <TableHead>{t('fichaPaciente.columnas.examen')}</TableHead>
              <TableHead>{t('fichaPaciente.columnas.categoria')}</TableHead>
              <TableHead>{t('fichaPaciente.columnas.estado')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {listaSolicitudes.map((s) => (
              <TableRow key={s.id}>
                <TableCell>{s.authoredOn ? new Date(s.authoredOn).toLocaleDateString('es-EC') : '—'}</TableCell>
                <TableCell>
                  <button type="button" className="text-left text-sm text-hce-pine underline-offset-2 hover:underline" onClick={() => setSolicitudAbierta(s)}>
                    {s.code?.text ?? '—'}
                  </button>
                </TableCell>
                <TableCell>{s.category?.[0]?.text ?? '—'}</TableCell>
                <TableCell>
                  <Badge variant={VARIANTE_ESTADO_ORDEN[s.status ?? ''] ?? 'secondary'}>
                    {s.status ? t(`fichaPaciente.estadoSolicitud.${s.status}`, s.status) : '—'}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <h5 className="mt-2 text-sm font-semibold">{t('fichaPaciente.informes')}</h5>
      {cargandoInformes ? (
        <p className="text-sm text-muted-foreground">{t('fichaPaciente.cargando')}</p>
      ) : listaInformes.length === 0 ? (
        <TablaVacia mensaje={t('fichaPaciente.sinDatos')} />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('fichaPaciente.columnas.fecha')}</TableHead>
              <TableHead>{t('fichaPaciente.columnas.examen')}</TableHead>
              <TableHead>{t('fichaPaciente.columnas.conclusion')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {listaInformes.map((d) => (
              <TableRow key={d.id}>
                <TableCell>{d.issued ? new Date(d.issued).toLocaleDateString('es-EC') : '—'}</TableCell>
                <TableCell>
                  <button type="button" className="text-left text-sm text-hce-pine underline-offset-2 hover:underline" onClick={() => setInformeAbierto(d)}>
                    {d.code?.text ?? '—'}
                  </button>
                </TableCell>
                <TableCell>{d.conclusion ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <DetalleSolicitudDialog solicitud={solicitudAbierta} onOpenChange={(v) => !v && setSolicitudAbierta(null)} />
      <DetalleInformeDialog informe={informeAbierto} onOpenChange={(v) => !v && setInformeAbierto(null)} />
    </div>
  );
}
